import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@packages/utils';
import {
  EWorkshopPartType,
  EWorkshopStatus,
  TPartsUI,
} from 'data/types/workshops.types';
import dayjs from 'dayjs';
import {
  useEditPartDateMutation,
  useEditPartTimeMutation,
} from 'redux/apiSlice/workshops.slice';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import { TRtkErrorType } from 'data/types/generic.types';
import { Flex } from 'antd';
import useGetValidationData from 'components/Base/Form/validationRules';
import { CalendarOutline } from 'assets/icon/icon';
import { Text } from 'components/Base/Typography';
import CalendarDatePicker from 'components/Shared/CalendarDatePicker/CalendarDatePicker';
import { useReportPartTimeError } from '../../../../context/PartTimeValidationContext';
import {
  ErrorText,
  TimeFieldWrapper,
  WorkshopDatePickerWrapper,
  WorkshopTimePicker,
} from './PartDateInput.styles';

type TTimeOrderErrors = {
  endError: string | null;
};

const noTimeOrderErrors: TTimeOrderErrors = {
  endError: null,
};

const PartDateInput: FC<{
  part: TPartsUI;
  workshopId: string;
  nextPartDateTime?: string;
  previousPartDateTime?: string;
  workshopStatus?: EWorkshopStatus;
}> = ({
  part,
  workshopId,
  previousPartDateTime,
  nextPartDateTime,
  workshopStatus,
}) => {
  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.workshops.details.sessions.view.actions.update.date',
  });
  const { t: tDateTime } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.workshops.details.basic-info.parts-date',
  });
  const { t: tEnum } = useTranslation({
    keyPrefix: 'enums',
  });
  const { EValidationMessages } = useGetValidationData();
  const validationMessages = useMemo(
    () => ({
      END_START_SHOULD_DIFFER: EValidationMessages.END_START_SHOULD_DIFFER,
      END_SHOULD_BE_LATER: EValidationMessages.END_SHOULD_BE_LATER,
    }),
    // Message strings are locale-stable for a mounted component; the containing
    // hook returns a new object on every render, so pin identity to the values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      EValidationMessages.END_START_SHOULD_DIFFER,
      EValidationMessages.END_SHOULD_BE_LATER,
    ],
  );
  const [editPartDate] = useEditPartDateMutation();
  const [editPartTime] = useEditPartTimeMutation();

  const [localStartTime, setLocalStartTime] = useState<string | null>(
    part.startTime || null,
  );
  const [localEndTime, setLocalEndTime] = useState<string | null>(
    part.endTime || null,
  );

  const validateTimeOrder = useCallback(
    (start: string | null, end: string | null): TTimeOrderErrors => {
      if (!start || !end) return noTimeOrderErrors;
      const startDayjs = dayjs(start, 'HH:mm:ss');
      const endDayjs = dayjs(end, 'HH:mm:ss');
      if (endDayjs.isSame(startDayjs, 'minute')) {
        return { endError: validationMessages.END_START_SHOULD_DIFFER };
      }
      if (endDayjs.isBefore(startDayjs, 'minute')) {
        return { endError: validationMessages.END_SHOULD_BE_LATER };
      }
      return noTimeOrderErrors;
    },
    [validationMessages],
  );

  const [{ endError }, setTimeErrors] = useState<TTimeOrderErrors>(() =>
    validateTimeOrder(part.startTime || null, part.endTime || null),
  );

  useEffect(() => {
    const nextStart = part.startTime || null;
    const nextEnd = part.endTime || null;
    setLocalStartTime(nextStart);
    setLocalEndTime(nextEnd);
    setTimeErrors(validateTimeOrder(nextStart, nextEnd));
  }, [part.startTime, part.endTime, validateTimeOrder]);

  // An invalid pair is shown but never saved, so the server data the Publish
  // button reads still looks valid. Publish it up so the button can grey out
  // while the error is on screen, and release it on unmount — leaving the tab
  // discards the unsaved value too (GROW-4212).
  const reportPartTimeError = useReportPartTimeError();
  const { workshopPartId } = part;
  useEffect(() => {
    reportPartTimeError(workshopPartId, endError);
    return () => reportPartTimeError(workshopPartId, null);
  }, [reportPartTimeError, workshopPartId, endError]);

  const needToDisable = workshopStatus === EWorkshopStatus.PUBLISHED;
  const { showToastNotification } = useShowToastNotification();

  const shouldDisableDatePicker = () => {
    if (part.date?.allowedOperations.readonly) return true;
    if (!needToDisable) return false;

    // If we have a current date value, check if it would be disabled
    if (part.date?.value) {
      const currentDate = dayjs(part.date.value);
      const nextPart = dayjs(nextPartDateTime);
      const prevPart = dayjs(previousPartDateTime);
      const dateNow = dayjs();

      const disableDaysBeforeNow = currentDate.isBefore(dateNow);
      const disableAfterNextPart =
        currentDate.isAfter(nextPart) || currentDate.isSame(nextPart);
      const disableBeforePrevPart =
        currentDate.isBefore(prevPart) || currentDate.isSame(prevPart);

      if (!previousPartDateTime && nextPartDateTime) {
        return disableAfterNextPart || disableDaysBeforeNow;
      }
      if (previousPartDateTime && !nextPartDateTime) {
        return disableBeforePrevPart || disableDaysBeforeNow;
      }
      if (previousPartDateTime && nextPartDateTime) {
        return (
          disableBeforePrevPart || disableAfterNextPart || disableDaysBeforeNow
        );
      }
      return disableDaysBeforeNow;
    }

    return needToDisable;
  };

  // Base rule for the date picker (existing logic)
  const isDatePickerDisabled = shouldDisableDatePicker();

  // New enabling/disabling logic for time pickers based on selected values
  const hasDateSelected = Boolean(part.date?.value);
  const hasStartTimeSelected = Boolean(localStartTime);

  // Global rule: if the date picker is disabled due to status/readonly, time pickers should also be disabled.
  // Additionally, time pickers are disabled when there is no date selected.
  const isStartTimeDisabled =
    isDatePickerDisabled || !hasDateSelected || needToDisable; // keep original publish rule

  // End time requires both date and start time to be selected
  const isEndTimeDisabled =
    isDatePickerDisabled ||
    !hasDateSelected ||
    !hasStartTimeSelected ||
    needToDisable; // keep original publish rule

  const label =
    part.partType !== EWorkshopPartType.STANDALONE
      ? `${tEnum(`workshop-part-type.${part.partType}`)}`
      : `${tDateTime('LABEL')}`;

  const onEditDate = async (value: string | null) => {
    if (!value) return;

    try {
      await editPartDate({
        dateTime: value,
        workshopId,
        workshopPartId: part.workshopPartId,
      }).unwrap();
      showToastNotification({
        message: t('SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (e) {
      showToastNotification({
        message: (e as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  };

  const onEditTime = async (
    timeType: 'START_TIME' | 'END_TIME',
    value: string | null,
  ) => {
    if (!value) return;

    const nextStart = timeType === 'START_TIME' ? value : localStartTime;
    const nextEnd = timeType === 'END_TIME' ? value : localEndTime;

    if (timeType === 'START_TIME') {
      setLocalStartTime(value);
    } else {
      setLocalEndTime(value);
    }

    const nextErrors = validateTimeOrder(nextStart, nextEnd);
    setTimeErrors(nextErrors);
    if (nextErrors.endError) return;

    try {
      await editPartTime({
        time: value,
        timeType,
        workshopId,
        workshopPartId: part.workshopPartId,
      }).unwrap();
      showToastNotification({
        message: t('SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (e) {
      // Revert local state on error
      if (timeType === 'START_TIME') {
        setLocalStartTime(part.startTime || null);
      } else {
        setLocalEndTime(part.endTime || null);
      }
      showToastNotification({
        message: (e as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  };

  return (
    <Flex
      align="center"
      data-cy="workshop-date-input"
      gap={10}
      style={{ paddingBottom: 20 }}
    >
      <Flex align="center" gap={8} style={{ width: 160 }}>
        <CalendarOutline size={24} />
        <Text color="#808080">{label}</Text>
      </Flex>

      <WorkshopDatePickerWrapper>
        <CalendarDatePicker
          value={
            part.date?.value
              ? dayjs(part.date.value).format('YYYY-MM-DD')
              : undefined
          }
          buttonLabel="Select Date"
          disabled={isDatePickerDisabled}
          placeholder="Select Date"
          onDateChange={onEditDate}
          format="YYYY-MM-DD"
        />
      </WorkshopDatePickerWrapper>

      <Flex align="center" gap={8}>
        <Text color="#808080">From</Text>
        <WorkshopTimePicker
          value={localStartTime ? dayjs(localStartTime, 'HH:mm') : undefined}
          disabled={isStartTimeDisabled}
          placeholder="Select Time"
          format="h:mm A"
          minuteStep={15}
          use12Hours
          showNow={false}
          hideDisabledOptions
          onChange={time =>
            onEditTime('START_TIME', time ? time.format('HH:mm:ss') : null)
          }
        />
      </Flex>

      <TimeFieldWrapper>
        <Flex align="center" gap={8}>
          <Text color="#808080">To</Text>
          <WorkshopTimePicker
            value={localEndTime ? dayjs(localEndTime, 'HH:mm') : undefined}
            disabled={isEndTimeDisabled}
            placeholder="Select Time"
            format="h:mm A"
            minuteStep={15}
            use12Hours
            showNow={false}
            hideDisabledOptions
            onChange={time =>
              onEditTime('END_TIME', time ? time.format('HH:mm:ss') : null)
            }
          />
        </Flex>
        {endError && (
          <ErrorText data-cy="workshop-end-time-error">{endError}</ErrorText>
        )}
      </TimeFieldWrapper>
    </Flex>
  );
};

export default PartDateInput;
